/**
 * Write-only xterm.js viewer fed from the `log` chunks in order of `seq`, with a
 * follow toggle. When the chunks have expired (30-day TTL) it falls back to the
 * archived object in Storage. Colors and carriage-return progress render because
 * the host captures step output through a pty (host-instruction-protocol spec).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { collection, onSnapshot, orderBy, query, type Firestore } from 'firebase/firestore';
import { COLLECTION, LOG_CHUNK_FIELD, SUBCOLLECTION } from '@finvari/deployer';
import { useArtifact } from '../hooks';

export interface LogViewerProps {
    db: Firestore;
    instructionId: string;
    /** Archived object path; used when live chunks are gone. */
    archivePath?: string;
    /** True while more chunks may still arrive (instruction not finished). */
    live: boolean;
}

export function LogViewer({ db, instructionId, archivePath, live }: LogViewerProps) {
    const holder = useRef<HTMLDivElement | null>(null);
    const term = useRef<Terminal | null>(null);
    const fit = useRef<FitAddon | null>(null);
    const written = useRef<number>(-1);
    const [follow, setFollow] = useState(true);
    const [chunksSeen, setChunksSeen] = useState(0);

    // Only fetch the archive when there are no live chunks (finished + expired).
    const archive = useArtifact(!live && chunksSeen === 0 ? archivePath : undefined);

    // Create the terminal once.
    useEffect(() => {
        if (!holder.current) return;
        const t = new Terminal({ convertEol: true, disableStdin: true, fontSize: 12, scrollback: 100_000 });
        const f = new FitAddon();
        t.loadAddon(f);
        t.open(holder.current);
        f.fit();
        term.current = t;
        fit.current = f;
        const onResize = () => f.fit();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            t.dispose();
            term.current = null;
        };
    }, []);

    // Stream chunks in seq order; write only the ones not yet written.
    useEffect(() => {
        const q = query(
            collection(db, `${COLLECTION.instructions}/${instructionId}/${SUBCOLLECTION.log}`),
            orderBy(LOG_CHUNK_FIELD.seq, 'asc'),
        );
        return onSnapshot(q, (snap) => {
            setChunksSeen(snap.size);
            for (const d of snap.docs) {
                const seq = d.get(LOG_CHUNK_FIELD.seq) as number;
                if (seq > written.current) {
                    term.current?.write(d.get(LOG_CHUNK_FIELD.text) as string);
                    written.current = seq;
                }
            }
            if (follow) term.current?.scrollToBottom();
        });
    }, [db, instructionId, follow]);

    // When falling back to the archive, write it once.
    useEffect(() => {
        if (archive.text && term.current && written.current < 0) {
            term.current.write(archive.text);
            written.current = 0;
        }
    }, [archive.text]);

    const emptyNote = useMemo(() => {
        if (chunksSeen === 0 && !live && !archivePath) return 'No log was recorded for this instruction.';
        if (archive.loading) return 'Loading archived log…';
        if (archive.error) return `Could not load archived log: ${archive.error}`;
        return undefined;
    }, [chunksSeen, live, archivePath, archive.loading, archive.error]);

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant={follow ? 'contained' : 'outlined'} onClick={() => setFollow((v) => !v)}>
                    {follow ? 'Following' : 'Follow'}
                </Button>
            </Stack>
            <Box ref={holder} sx={{ height: 480, bgcolor: '#000', p: 1, borderRadius: 1 }} />
            {emptyNote && <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{emptyNote}</Box>}
        </Stack>
    );
}
